import type { DateTimeRangeValue, Dayjs, TimeFormat } from 'Utils/dateTime';
import {
  convertDateFormatToDayjs,
  convertTimeFormatToDayjs,
  convertTimeToFormat,
  convertUtcToTimezone,
  createDayjsInstance,
  createTimezoneDayjs,
  createUtcDayjs,
  DateFormat,
  formatDayjsToString,
  generateTempId,
  getCurrentDateIso,
  isValidDayjs,
  parseDateString,
  parseTimeString,
  TimeFormat as TimeFormatEnum
} from 'Utils/dateTime';

import type { CourseDateEntry } from '../forms';

// ============================================================================
// COURSE DATE CONVERSION UTILITIES
// ============================================================================

/**
 * Converts course date entries from one format to another
 */
export const convertCourseDatesFormat = <
  T extends { startDate: string; endDate: string; startTime: string; endTime: string }
>(
  courseDates: T[],
  fromDateFormat: DateFormat,
  fromTimeFormat: TimeFormat,
  toDateFormat: DateFormat,
  toTimeFormat: TimeFormat
): T[] => {
  const fromDayjsDateFormat = convertDateFormatToDayjs(fromDateFormat);
  const fromDayjsTimeFormat = convertTimeFormatToDayjs(fromTimeFormat);
  const toDayjsDateFormat = convertDateFormatToDayjs(toDateFormat);
  const toDayjsTimeFormat = convertTimeFormatToDayjs(toTimeFormat);

  return courseDates.map(courseDate => {
    const startDateParsed = parseDateString(courseDate.startDate, fromDayjsDateFormat);
    const endDateParsed = parseDateString(courseDate.endDate, fromDayjsDateFormat);
    const startTimeParsed = parseTimeString(courseDate.startTime, fromDayjsTimeFormat);
    const endTimeParsed = parseTimeString(courseDate.endTime, fromDayjsTimeFormat);

    const newStartDate = startDateParsed
      ? startDateParsed.format(toDayjsDateFormat)
      : courseDate.startDate;
    const newEndDate = endDateParsed ? endDateParsed.format(toDayjsDateFormat) : courseDate.endDate;
    const newStartTime = startTimeParsed
      ? startTimeParsed.format(toDayjsTimeFormat)
      : courseDate.startTime;
    const newEndTime = endTimeParsed ? endTimeParsed.format(toDayjsTimeFormat) : courseDate.endTime;

    return {
      ...courseDate,
      startDate: newStartDate,
      endDate: newEndDate,
      startTime: newStartTime,
      endTime: newEndTime
    };
  });
};

/**
 * Converts CourseDateEntry to DateTimeRangeValue for display
 */
export const convertCourseDateToDateTimeRange = (
  courseDate: CourseDateEntry,
  courseTimezone: string,
  dateFormat: DateFormat,
  timeFormat: TimeFormat
): DateTimeRangeValue => {
  const dayjsDateFormat = convertDateFormatToDayjs(dateFormat);
  const dayjsTimeFormat = convertTimeFormatToDayjs(timeFormat);

  if (courseDate.startDate.includes('Z') && !courseDate.startDate.includes('+')) {
    const startUtc = createUtcDayjs(courseDate.startDate);
    const endUtc = createUtcDayjs(courseDate.endDate);

    if (!startUtc.isValid() || !endUtc.isValid()) {
      return {
        startDate: '',
        startTime: '',
        endDate: '',
        endTime: ''
      };
    }

    const startInCourseTz = convertUtcToTimezone(courseDate.startDate, courseTimezone);
    const endInCourseTz = convertUtcToTimezone(courseDate.endDate, courseTimezone);

    if (!isValidDayjs(startInCourseTz) || !isValidDayjs(endInCourseTz)) {
      return {
        startDate: '',
        startTime: '',
        endDate: '',
        endTime: ''
      };
    }

    const result = {
      startDate: formatDayjsToString(startInCourseTz, dayjsDateFormat),
      startTime: formatDayjsToString(startInCourseTz, dayjsTimeFormat),
      endDate: formatDayjsToString(endInCourseTz, dayjsDateFormat),
      endTime: formatDayjsToString(endInCourseTz, dayjsTimeFormat)
    };

    return result;
  }

  const result = {
    startDate: courseDate.startDate || '',
    startTime: courseDate.startTime || '',
    endDate: courseDate.endDate || '',
    endTime: courseDate.endTime || ''
  };

  return result;
};

/**
 * Converts CourseDateEntry to user timezone DateTimeRangeValue
 */
export const convertCourseDateToUserTimezoneDateTimeRange = (
  courseDate: CourseDateEntry,
  userTimezone: string,
  dateFormat: string,
  timeFormat: string
): DateTimeRangeValue => {
  const dayjsDateFormat = convertDateFormatToDayjs(dateFormat as DateFormat);
  const dayjsTimeFormat = convertTimeFormatToDayjs(timeFormat as TimeFormat);

  const startDate = new Date(courseDate.startDate);
  const endDate = new Date(courseDate.endDate);

  const startInUserTz = createTimezoneDayjs(startDate, userTimezone);
  const endInUserTz = createTimezoneDayjs(endDate, userTimezone);

  if (!isValidDayjs(startInUserTz) || !isValidDayjs(endInUserTz)) {
    return {
      startDate: '',
      startTime: '',
      endDate: '',
      endTime: ''
    };
  }

  return {
    startDate: formatDayjsToString(startInUserTz, dayjsDateFormat),
    startTime: formatDayjsToString(startInUserTz, dayjsTimeFormat),
    endDate: formatDayjsToString(endInUserTz, dayjsDateFormat),
    endTime: formatDayjsToString(endInUserTz, dayjsTimeFormat)
  };
};

// ============================================================================
// COURSE DATE TO UTC CONVERSION
// ============================================================================

/**
 * Converts CourseDateEntry array to UTC format for backend submission
 */
export const convertCourseDatesToUtcFormat = (
  courseDates: CourseDateEntry[],
  courseTimezone: string = 'UTC',
  dateFormat: DateFormat = DateFormat.YYYY_MM_DD,
  timeFormat: TimeFormat = TimeFormatEnum.H24
): Array<{ startDate: string; endDate: string }> =>
  courseDates.map(courseDate => {
    // If already in UTC format
    if (courseDate.startDate.includes('Z') && !courseDate.startDate.includes('+')) {
      return {
        startDate: courseDate.startDate,
        endDate: courseDate.endDate
      };
    }

    // Parse date and time using the provided format
    const dayjsDateFormat = convertDateFormatToDayjs(dateFormat);
    const dayjsTimeFormat = convertTimeFormatToDayjs(timeFormat);

    const startDateParsed = parseDateString(courseDate.startDate, dayjsDateFormat);
    const endDateParsed = parseDateString(courseDate.endDate, dayjsDateFormat);
    const startTimeParsed = parseTimeString(courseDate.startTime, dayjsTimeFormat);
    const endTimeParsed = parseTimeString(courseDate.endTime, dayjsTimeFormat);

    if (!startDateParsed || !endDateParsed || !startTimeParsed || !endTimeParsed) {
      return {
        startDate: '',
        endDate: ''
      };
    }

    // Combine date and time
    const startDateTime = `${startDateParsed.format('YYYY-MM-DD')}T${startTimeParsed.format('HH:mm:ss')}`;
    const endDateTime = `${endDateParsed.format('YYYY-MM-DD')}T${endTimeParsed.format('HH:mm:ss')}`;

    const startInCourseTz = createTimezoneDayjs(startDateTime, courseTimezone);
    const endInCourseTz = createTimezoneDayjs(endDateTime, courseTimezone);

    if (!isValidDayjs(startInCourseTz) || !isValidDayjs(endInCourseTz)) {
      return {
        startDate: '',
        endDate: ''
      };
    }

    const startUtc = startInCourseTz.utc();
    const endUtc = endInCourseTz.utc();

    const result = {
      startDate: startUtc.toISOString(),
      endDate: endUtc.toISOString()
    };

    return result;
  });

// ============================================================================
// COURSE DATE CREATION UTILITIES
// ============================================================================

/**
 * Creates a new CourseDateEntry with proper defaults
 */
export const createNewCourseDateEntry = (
  startDate = '',
  endDate = '',
  courseTimezone?: string,
  previousEndDate?: string,
  options: {
    defaultStartTime?: string;
    defaultEndTime?: string;
    dateFormat?: DateFormat;
    timeFormat?: TimeFormat;
  } = {}
): CourseDateEntry => {
  const {
    defaultStartTime = '09:00',
    defaultEndTime = '16:00',
    dateFormat = DateFormat.YYYY_MM_DD,
    timeFormat = TimeFormatEnum.H24
  } = options;
  const effectiveCourseTimezone = courseTimezone ?? 'UTC';
  const dayjsDateFormat = convertDateFormatToDayjs(dateFormat);
  let targetDate: string;

  const formattedDefaultStartTime = convertTimeToFormat(defaultStartTime, timeFormat);
  const formattedDefaultEndTime = convertTimeToFormat(defaultEndTime, timeFormat);

  if (previousEndDate) {
    const prevEndDateInCourseTz = createUtcDayjs(previousEndDate).tz(effectiveCourseTimezone);
    const nextDayInCourseTz = prevEndDateInCourseTz.add(1, 'day');

    if (!isValidDayjs(nextDayInCourseTz)) {
      targetDate = getCurrentDateIso();
    } else {
      targetDate = nextDayInCourseTz.format(dayjsDateFormat);
    }
  } else {
    targetDate = getCurrentDateIso();
  }

  if (startDate && endDate) {
    return {
      id: generateTempId(),
      startDate,
      endDate,
      startTime: formattedDefaultStartTime,
      endTime: formattedDefaultEndTime
    };
  }

  const result = {
    id: generateTempId(),
    startDate: targetDate,
    endDate: targetDate,
    startTime: formattedDefaultStartTime,
    endTime: formattedDefaultEndTime
  };

  return result;
};

// ============================================================================
// COURSE DATE TIMEZONE CONVERSION UTILITIES
// ============================================================================

/**
 * Converts DateTimeRangeValue to ISO with timezone
 */
export const convertDateTimeRangeToIsoWithTimezone = (
  dateTimeRange: DateTimeRangeValue,
  courseTimezone: string
): { startIso: string; endIso: string } => {
  const startDateTime = `${dateTimeRange.startDate}T${dateTimeRange.startTime}`;
  const endDateTime = `${dateTimeRange.endDate}T${dateTimeRange.endTime}`;

  const startInCourseTz = createTimezoneDayjs(startDateTime, courseTimezone);
  const endInCourseTz = createTimezoneDayjs(endDateTime, courseTimezone);

  if (!isValidDayjs(startInCourseTz) || !isValidDayjs(endInCourseTz)) {
    return {
      startIso: '',
      endIso: ''
    };
  }

  return {
    startIso: startInCourseTz.utc().toISOString(),
    endIso: endInCourseTz.utc().toISOString()
  };
};

/**
 * Converts DateTimeRangeValue to course timezone ISO format
 */
export const convertDateTimeRangeToCourseTimezoneIso = (
  dateTimeRange: DateTimeRangeValue,
  courseTimezone: string,
  dateFormat: string
): { startIso: string; endIso: string } => {
  const dayjsDateFormat = convertDateFormatToDayjs(dateFormat as DateFormat);

  const startDateTime = `${dateTimeRange.startDate}T${dateTimeRange.startTime}`;
  const endDateTime = `${dateTimeRange.endDate}T${dateTimeRange.endTime}`;

  const startInCourseTz = createTimezoneDayjs(startDateTime, courseTimezone);
  const endInCourseTz = createTimezoneDayjs(endDateTime, courseTimezone);

  if (!isValidDayjs(startInCourseTz) || !isValidDayjs(endInCourseTz)) {
    return {
      startIso: '',
      endIso: ''
    };
  }

  const isoFormat = `${dayjsDateFormat}THH:mm:ss.SSS[Z]`;

  return {
    startIso: startInCourseTz.format(isoFormat),
    endIso: endInCourseTz.format(isoFormat)
  };
};

/**
 * Converts DateTimeRangeValue to timezone-aware format
 */
export const convertDateTimeRangeToTimezoneAware = (
  dateTimeRange: DateTimeRangeValue,
  courseTimezone: string,
  dateFormat: string,
  timeFormat: string
): { startDate: string; endDate: string } => {
  const dayjsDateFormat = convertDateFormatToDayjs(dateFormat as DateFormat);
  const dayjsTimeFormat = convertTimeFormatToDayjs(timeFormat as TimeFormat);

  const startDateTime = `${dateTimeRange.startDate}T${dateTimeRange.startTime}`;
  const endDateTime = `${dateTimeRange.endDate}T${dateTimeRange.endTime}`;

  const startInCourseTz = createTimezoneDayjs(startDateTime, courseTimezone);
  const endInCourseTz = createTimezoneDayjs(endDateTime, courseTimezone);

  if (!isValidDayjs(startInCourseTz) || !isValidDayjs(endInCourseTz)) {
    return {
      startDate: '',
      endDate: ''
    };
  }

  const timezoneAwareFormat = `${dayjsDateFormat} ${dayjsTimeFormat}`;

  return {
    startDate: formatDayjsToString(startInCourseTz, timezoneAwareFormat),
    endDate: formatDayjsToString(endInCourseTz, timezoneAwareFormat)
  };
};

/**
 * Converts local DateTimeRangeValue to ISO
 */
export const convertLocalDateTimeRangeToIso = (
  dateTimeRange: DateTimeRangeValue
): { startIso: string; endIso: string } => {
  const startDateTime = `${dateTimeRange.startDate}T${dateTimeRange.startTime}`;
  const endDateTime = `${dateTimeRange.endDate}T${dateTimeRange.endTime}`;

  const startDayjs = createDayjsInstance(startDateTime);
  const endDayjs = createDayjsInstance(endDateTime);

  if (!isValidDayjs(startDayjs) || !isValidDayjs(endDayjs)) {
    return {
      startIso: '',
      endIso: ''
    };
  }

  return {
    startIso: startDayjs.toISOString(),
    endIso: endDayjs.toISOString()
  };
};

/**
 * Converts CourseDateEntry array to timezone-aware format
 */
export const convertCourseDatesToTimezoneAware = (
  courseDates: CourseDateEntry[],
  courseTimezone: string
): CourseDateEntry[] => {
  return courseDates.map(courseDate => {
    let startInCourseTz: Dayjs;
    let endInCourseTz: Dayjs;

    if (courseDate.startDate.includes('Z') && !courseDate.startDate.includes('+')) {
      startInCourseTz = createTimezoneDayjs(courseDate.startDate, courseTimezone);
      endInCourseTz = createTimezoneDayjs(courseDate.endDate, courseTimezone);
    } else {
      const startDate = courseDate.startDate.split('T')[0];
      const startTime = courseDate.startDate.split('T')[1]?.split('.')[0] || '00:00:00';
      const endDate = courseDate.endDate.split('T')[0];
      const endTime = courseDate.endDate.split('T')[1]?.split('.')[0] || '00:00:00';

      const startDateTime = `${startDate}T${startTime}`;
      const endDateTime = `${endDate}T${endTime}`;

      startInCourseTz = createTimezoneDayjs(startDateTime, courseTimezone);
      endInCourseTz = createTimezoneDayjs(endDateTime, courseTimezone);
    }

    return {
      ...courseDate,
      startDate: startInCourseTz.utc().toISOString(),
      endDate: endInCourseTz.utc().toISOString()
    };
  });
};
